import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { getEntityFieldPermission } from '@/api/entity/field-permissions';
import { _getSingleEntityOptions } from '@/api/entity/queries';
import { Permission } from '@/api/permissions';
import { usePermissions } from '@/shared/modules/permissions';
import { useEntitySlice } from '@/stores/selectors';

export const ENTITY_FIELD_ACCESS = {
  /** The caller holds the gating permission — editing applies the change directly. */
  full: 'full',
  /** The caller does not hold it — the field must not be editable. */
  denied: 'denied',
} as const;

export type EntityFieldAccess = (typeof ENTITY_FIELD_ACCESS)[keyof typeof ENTITY_FIELD_ACCESS];

export const FIELD_DENIED_TOOLTIP = "You don't have permission to change this field.";

export type UseEntityFieldAccessResult = {
  accessOf: (_field: string) => EntityFieldAccess;
  /** Copy explaining a denial, or `undefined` while the permission list is unknown. */
  deniedReasonFor: (_field: string) => string | undefined;
  isLoading: boolean;
};

/**
 * Per-field editability for the entity profile form, in the two tiers the
 * backend applies: `EntityManageProfile` gates the PUT at any approval status
 * (`UpdateEntityHandler`), and the field-to-permission map
 * (`ENTITY_FIELD_UPDATE_PERMISSIONS`) applies on top once an approved version
 * exists (`Entity.update()`).
 *
 * UX affordance only — the backend enforces both tiers independently.
 */
export const useEntityFieldAccess = (): UseEntityFieldAccessResult => {
  const { has, isLoading } = usePermissions();
  const { stepForms } = useEntitySlice();
  const params = useParams();
  const { isSuccess: isSavedEntityLoaded } = useQuery(_getSingleEntityOptions(params.id));
  const hasSavedEntity = Boolean(params.id || stepForms.id);
  const isSavedEntityHydrated = isSavedEntityLoaded && stepForms.id === params.id;
  const isSavedEntityUnresolved = Boolean(params.id) && !isSavedEntityHydrated;
  const shouldApplyFieldPermissions = hasSavedEntity && Boolean(stepForms.isApproved);

  return useMemo(() => {
    const accessOf = (field: string): EntityFieldAccess => {
      if (hasSavedEntity && !has(Permission.EntityManageProfile)) return ENTITY_FIELD_ACCESS.denied;

      const permission = getEntityFieldPermission(field);

      if (!permission) return ENTITY_FIELD_ACCESS.full;

      if (isSavedEntityUnresolved) return ENTITY_FIELD_ACCESS.denied;

      if (!shouldApplyFieldPermissions) return ENTITY_FIELD_ACCESS.full;

      return has(permission) ? ENTITY_FIELD_ACCESS.full : ENTITY_FIELD_ACCESS.denied;
    };

    return {
      accessOf,
      deniedReasonFor: (field: string) =>
        !isLoading && !isSavedEntityUnresolved && accessOf(field) === ENTITY_FIELD_ACCESS.denied
          ? FIELD_DENIED_TOOLTIP
          : undefined,
      isLoading,
    };
  }, [has, isLoading, hasSavedEntity, isSavedEntityUnresolved, shouldApplyFieldPermissions]);
};
